import { useEffect, useState } from 'react';
import { API, socketEvents, TrackerDataType, transitionTime } from '../constants/constants';
import { EngagementKeys } from '../constants/IssueAndServiceKeys';
import { htmlToText } from 'html-to-text';
import { Socket } from 'socket.io-client';
import {
  DropdownTable,
  Engagement,
  EngagementQueryAdder,
  EngagementRequestPayload,
  File as TrackerFile,
  FlatEngagement,
  UpdateEngagementWithFileFieldBody,
  UpdateItemWithFileRemovingFile,
  UpdateItemWithFileRemovingURL,
  UpdateItemWithFileUploadingURL,
} from '../types';
import { openSnackbar } from '../../components/AppSnackbar/snackbarSlice';
import axios, { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import dayjs from '../../utils/dayjs';
import { normalizeToIds } from '../../utils/eventHandlerUtils';

const buildEngagementRequestPayload = (payload: Engagement): EngagementRequestPayload => {
  const requestPayload = {
    ...payload,
    communityMembers: normalizeToIds(payload.communityMember),
    organizations: normalizeToIds(payload.organization),
  };
  delete requestPayload.communityMember;
  delete requestPayload.organization;
  return requestPayload;
};

const EngagementLogEventHandler = (socket: Socket) => {
  const dispatch = useDispatch();

  return {
    type: TrackerDataType.ENGAGEMENT,
    newItemModalTitle: 'Add a New Engagement',
    createNewItem: async (fields: Engagement, files: File[], successCallback: (arg0: { postId: string }) => void) => {
      try {
        const requestPayload = buildEngagementRequestPayload(fields);
        let response = await API.post(`/engagements`, [requestPayload]);
        if (response.status !== 201) {
          dispatch(
            openSnackbar({
              severity: 'alert',
              message: response.statusText,
            })
          );
          return response;
        }

        let f = new FormData();
        const id = response.data.id[0] || null;
        const headers = { 'content-type': 'multipart/form-data' };

        files
          .filter((file) => file.type !== 'url')
          .forEach((file) => {
            f.append('file', file);
          });

        if (id) {
          response = await API.post(`/engagement/${id}/upload`, f, {
            headers: headers,
          });

          if (response.status !== 201) {
            dispatch(
              openSnackbar({
                severity: 'warning',
                message: `File upload failed for newly created engagement`,
              })
            );
          }

          await Promise.all(
            files
              .filter((file) => file.type === 'url')
              .map((file) => {
                return API.post(`/engagement/${id}/url`, file).then((response) => {
                  if (response.status !== 201) {
                    openSnackbar({
                      severity: 'warning',
                      message: `Url upload failed for newly created engagement`,
                    });
                  }
                });
              })
          );

          if (successCallback) {
            successCallback({ postId: id });
          }
        }

        return response;
      } catch (error: any) {
        if (axios instanceof AxiosError && error.response.status === 400) {
          dispatch(
            openSnackbar({
              severity: 'error',
              message: error.response.data.message,
            })
          );
        }
      }
    },
    // use as a React Hook
    useListenOnCreation: (rowId: string) => {
      const [isNewlyAdded, setIsNewlyAdded] = useState(false);

      useEffect(() => {
        let timeout: NodeJS.Timeout;
        const listener = ({ engagement }: { engagement: Engagement }) => {
          if (engagement._id === rowId) {
            setIsNewlyAdded(true);
            timeout = setTimeout(() => {
              setIsNewlyAdded(false);
            }, transitionTime);
          }
        };
        socket.on(socketEvents.EVENT_ENGAGEMENT_ADDED, listener);

        return () => {
          socket.off(socketEvents.EVENT_ENGAGEMENT_ADDED, listener);
          if (timeout) {
            clearTimeout(timeout);
          }
        };
      }, [rowId]);

      return { isNewlyAdded, setIsNewlyAdded };
    },
    editItem: (val: EngagementQueryAdder[keyof EngagementQueryAdder], index: string) => {
      let newVal;
      if (!val) {
        newVal = null;
      } else {
        switch (index) {
          case EngagementKeys.type:
            {
              let array: Engagement['type'] = [];
              (val as EngagementQueryAdder['type']).forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case EngagementKeys.linkedService:
            newVal = (val as EngagementQueryAdder['linkedService']).filter((item) => item !== null);
            break;
          case EngagementKeys.lead:
            {
              let array: Engagement['lead'] = [];
              (val as EngagementQueryAdder['lead']).forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case EngagementKeys.organization:
            newVal = (val as EngagementQueryAdder['organization'])?._id;
            //TODO cancel community member when organization changed
            break;
          case EngagementKeys.communityMember:
            newVal = (val as EngagementQueryAdder['communityMember'])?._id;
            break;
          case EngagementKeys.topic:
          case EngagementKeys.background:
          case EngagementKeys.notes:
          case EngagementKeys.entryDate:
          case EngagementKeys.dueDate:
          case EngagementKeys.date:
            newVal = val;
            break;
          case EngagementKeys.expert:
            newVal = val;
            break;
          case EngagementKeys.units:
          case EngagementKeys.campaign:
            let arr: Engagement['campaign'] = [];
            (val as EngagementQueryAdder['campaign']).forEach((obj) => arr.push(obj));
            newVal = arr;
            break;
          case EngagementKeys.file:
            newVal = val;
            break;
          case EngagementKeys.dept:
            newVal = (val as EngagementQueryAdder['dept']).map((c) => c.name);
            break;
          default:
            newVal = (val as EngagementQueryAdder['communityMember'])?.name;
            break;
        }
      }
      return newVal;
    },
    updateItem: async (updatedEngagement: Engagement, successCallback: (arg0: { postId: string }) => void) => {
      const normalizedEngagementData = buildEngagementRequestPayload(updatedEngagement);
      socket.emit(socketEvents.EVENT_REQ_UPDATE_ENGAGEMENT, { data: normalizedEngagementData }, successCallback);
    },
    updateItemWithFile: async (
      { id, fields }: UpdateEngagementWithFileFieldBody,
      uploadingFiles: File[],
      uploadingUrls: UpdateItemWithFileUploadingURL[],
      removingFiles: UpdateItemWithFileRemovingFile[],
      removingUrls: UpdateItemWithFileRemovingURL[]
    ) => {
      try {
        const formData = new FormData();
        for (const file of uploadingFiles) {
          formData.append('file', file);
        }
        formData.append('urls', JSON.stringify(uploadingUrls));
        formData.append('deletingFiles', JSON.stringify(removingFiles));
        formData.append('deletingUrls', JSON.stringify(removingUrls));
        const normalizedFields = buildEngagementRequestPayload(fields);
        formData.append('fields', JSON.stringify(normalizedFields));

        await API.put(`/engagements/${id}/update`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (err) {
        console.error(`failed to update service with files - ${err}`);
      }
    },
    deleteItem: async (id: string) => {
      socket.emit(socketEvents.EVENT_REQ_DELETE_ENGAGEMENT, id);
    },
    undoDelete: async (data: Engagement) => {
      try {
        socket.emit(socketEvents.EVENT_NEW_ENGAGEMENT, { data: data, undo: true });
      } catch (e: any) {
        alert(e.message);
      }
    },
    monthlyView: async (currMonth: string) => {
      socket.emit(socketEvents.EVENT_REQ_MONTHLY_ENGAGEMENTS, {
        month: currMonth,
        timezoneOffset: dayjs.tz.guess(),
      });
    },
    flattenForExport: (data: FlatEngagement[], dropdownTable: DropdownTable) => {
      return data.map((row: FlatEngagement) => {
        try {
          let flatRow = JSON.parse(JSON.stringify(row.cells));

          delete flatRow[EngagementKeys.date];
          flatRow[EngagementKeys.entryDate] = dayjs(flatRow[EngagementKeys.entryDate]).format('YYYY-MM-DD');
          flatRow[EngagementKeys.dueDate] = flatRow[EngagementKeys.dueDate]
            ? dayjs(flatRow[EngagementKeys.dueDate]).format('YYYY-MM-DD')
            : '';

          flatRow[EngagementKeys.type] = flatRow[EngagementKeys.type]
            .map((c: FlatEngagement['cells'][EngagementKeys.type][0]) => `${c.type}:${c.name}`)
            .join(', \n');

          flatRow[EngagementKeys.expert] = flatRow[EngagementKeys.expert]
            .map(
              (le: FlatEngagement['cells'][EngagementKeys.expert][0]) =>
                dropdownTable['expert'].find((dropdownle) => dropdownle._id === le._id)?.name ?? ''
            )
            .join(', \n');

          flatRow[EngagementKeys.lead] = flatRow[EngagementKeys.lead]
            .map(
              (leadId: FlatEngagement['cells'][EngagementKeys.lead][0]) =>
                dropdownTable['lead'].find((lead) => lead._id === leadId)?.name ?? null
            )
            .filter((leadName: string) => leadName !== null)
            .join(', ');

          flatRow[EngagementKeys.linkedService] = flatRow[EngagementKeys.linkedService].map(
            (c: FlatEngagement['cells'][EngagementKeys.linkedService][0]) => c.name
          );

          const communityMemberEntries = Array.isArray(row.cells[EngagementKeys.communityMember])
            ? row.cells[EngagementKeys.communityMember]
            : [];
          const resolvedCommunityMembers = communityMemberEntries
            .map((id: string) => dropdownTable.communityMember.find((cm) => cm._id === id))
            .filter(Boolean);

          flatRow[EngagementKeys.communityMember] = resolvedCommunityMembers
            .map((cm) => (cm ? cm.name : ''))
            .filter((names) => names !== '')
            .join(', \n');
          flatRow['Community Member Email'] = resolvedCommunityMembers
            .map((cm) => (cm ? cm.email : ''))
            .filter((emails) => emails !== '')
            .join(', \n');
          flatRow['Community Member Phone'] = resolvedCommunityMembers
            .map((cm) => (cm ? cm.phone : ''))
            .filter((phones) => phones !== '')
            .join(', \n');

          const organizationEntries = Array.isArray(row.cells[EngagementKeys.organization])
            ? row.cells[EngagementKeys.organization]
            : [];
          const resolvedOrganizations = organizationEntries
            .map((id: string) => dropdownTable.organization.find((organization) => organization._id === id))
            .filter(Boolean);
          flatRow[EngagementKeys.organization] = resolvedOrganizations
            .map((organization) => (organization ? organization.name : ''))
            .filter((names) => names !== '')
            .join(', \n');

          flatRow[EngagementKeys.file] = flatRow[EngagementKeys.file].map((file: TrackerFile) => {
            if (file.type === 'url') {
              return file.path;
            } else {
              return file.name;
            }
          });
          flatRow[EngagementKeys.campaign] = flatRow[EngagementKeys.campaign]
            .map((campaign: FlatEngagement['cells'][EngagementKeys.campaign][0]) => `${campaign.name}`)
            .join(', \n');

          let messaging = flatRow[EngagementKeys.notes] ? flatRow[EngagementKeys.notes] : '';
          messaging += flatRow['Field Addons']?.keyMessaging ? flatRow['Field Addons'].keyMessaging : '';
          messaging = htmlToText(messaging, { wordwrap: null });
          messaging = messaging.replace(/"/g, '""');
          flatRow[EngagementKeys.notes] = messaging;

          let response = flatRow[EngagementKeys.background] ? flatRow[EngagementKeys.background] : '';
          response += flatRow['Field Addons']?.response ? flatRow['Field Addons'].response : '';
          response = htmlToText(response, { wordwrap: null });
          response = response.replace(/"/g, '""');
          flatRow[EngagementKeys.background] = response;

          delete flatRow['Field Addons'];

          return flatRow;
        } catch {
          return {
            [EngagementKeys.entryDate]: '',
            [EngagementKeys.dueDate]: '',
            [EngagementKeys.contactMethod]: '',
            [EngagementKeys.topic]: 'error flattening data for export!',
            [EngagementKeys.type]: '',
            [EngagementKeys.units]: '',
            [EngagementKeys.expert]: '',
            [EngagementKeys.organization]: '',
            [EngagementKeys.communityMember]: '',
            'Community Member Email': '',
            'Community Member Phone': '',
            [EngagementKeys.campaign]: '',
            [EngagementKeys.lead]: '',
            [EngagementKeys.background]: '',
            [EngagementKeys.file]: '',
            [EngagementKeys.status]: '',
            [EngagementKeys.dept]: '',
            [EngagementKeys.notes]: '',
            [EngagementKeys.linkedService]: '',
          };
        }
      });
    },
  };
};

export default EngagementLogEventHandler;
